/**
 * Inline session-document picker for the Projects agent.
 *
 * Used when an action targets "a document" on the linked session but
 * which one is ambiguous (e.g. "export this session's note as a PDF"
 * when the session has several documents): the agent asks ``ask_user``
 * with ``kind: 'pick_document'`` and this list renders the session's
 * documents (self-fetched) so the clinician chooses which one to act
 * on. Mirrors the session page's document tabs, as a selectable list.
 *
 * Standalone + presentational: owns the fetch + selection highlight; the
 * parent owns the submit (the clarification's Continue posts the picked
 * ``document_id`` back to the agent). The fetch itself is the session
 * domain's, so it crosses as the ``useSessionDocuments`` hook port.
 */

import { TypographyCaption } from '@heidi/design-system/typography';
import { DocumentTabType } from '@heidi/schema/ml-scribe';
import { cn } from '@heidi/utils';
import { FileText, Loader2 } from 'lucide-react';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

export interface PickedDocument {
  id: string;
  name: string;
}

export interface SessionDocumentPickerListProps {
  /** Session whose documents are listed. */
  sessionId: string;
  /** Currently-armed document id (highlights the row). */
  selectedId?: string;
  onSelect: (document: PickedDocument) => void;
  disabled?: boolean;
  className?: string;
}

export interface SessionDocumentLike {
  id: string;
  name?: string | null;
  index?: number | null;
  created_at?: string | null;
  document_tab_type?: DocumentTabType | null;
}

/** What the app supplies: the session-documents query. */
export interface SessionDocumentPickerListHost {
  /** Called unconditionally with the listed session. Module-level, so its identity is stable. */
  useSessionDocuments: (sessionId: string) => {
    data?: { documents?: readonly SessionDocumentLike[] | null } | null;
    isLoading: boolean;
  };
}

const documentLabel = (doc: SessionDocumentLike): string =>
  (doc.name ?? '').trim() || 'Document';

const typeLabel = (doc: SessionDocumentLike): string | null => {
  switch (doc.document_tab_type) {
    case DocumentTabType.PDF:
      return 'PDF';
    case DocumentTabType.DICTATION:
      return 'Dictation';
    case DocumentTabType.PRECHART:
      return 'Pre-chart';
    default:
      return null;
  }
};

export const SessionDocumentPickerList = ({
  sessionId,
  selectedId,
  onSelect,
  disabled = false,
  className,
  useSessionDocuments,
}: SessionDocumentPickerListProps & SessionDocumentPickerListHost): JSX.Element => {
  const intl = useIntl();
  const { data, isLoading } = useSessionDocuments(sessionId);

  const documents = useMemo<SessionDocumentLike[]>(() => {
    const list = (data?.documents ?? []) as SessionDocumentLike[];
    // Match the session page's tab ordering: by ``index``, then recency.
    return [...list].sort(
      (a, b) =>
        (a.index ?? 0) - (b.index ?? 0) ||
        new Date(a.created_at ?? 0).getTime() - new Date(b.created_at ?? 0).getTime()
    );
  }, [data?.documents]);

  return (
    <div
      className={cn('flex flex-col gap-1', className)}
      data-testid="session-document-picker"
    >
      {isLoading ? (
        <div className="flex items-center gap-2 px-3 py-4 text-text-secondary">
          <Loader2 className="size-4 animate-spin" />
          <TypographyCaption>Loading documents…</TypographyCaption>
        </div>
      ) : documents.length === 0 ? (
        <TypographyCaption className="px-3 py-4 text-text-secondary">
          {intl.formatMessage({
            id: 'ujZVhgGUIx',
            defaultMessage: 'This session has no documents to choose from yet.',
          })}
        </TypographyCaption>
      ) : (
        <div className="flex max-h-64 flex-col gap-1 overflow-y-auto [scrollbar-gutter:stable]">
          {documents.map((doc) => {
            const isSelected = doc.id === selectedId;
            const type = typeLabel(doc);
            return (
              <button
                key={doc.id}
                type="button"
                disabled={disabled}
                onClick={() => onSelect({ id: doc.id, name: documentLabel(doc) })}
                aria-pressed={isSelected}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition-colors',
                  'disabled:cursor-not-allowed disabled:opacity-60',
                  isSelected
                    ? 'border-border-pressed bg-background-secondary'
                    : 'border-border bg-surface hover:bg-surface-hover'
                )}
                data-testid="session-document-picker-row"
              >
                <FileText className="size-4 shrink-0 text-text-secondary" />
                <span className="min-w-0 flex-1 truncate text-sm text-text-primary">
                  {documentLabel(doc)}
                </span>
                {type && (
                  <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-text-tertiary">
                    {type}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
